import { DeviceContext } from "../context/deviceContext";
import { RcContext } from "../context/rcContext";

export class MouseEventService {

    static getUntransformedX = (event: MouseEvent, scale: number): number => event.offsetX * scale;

    static getUntransformedY = (event: MouseEvent, scale: number): number => event.offsetY * scale;

    static getRotationInRadians = (deviceAngle: number): number => {
        return Math.PI * deviceAngle / 2;
    }

    static getX = (event: MouseEvent, scale: number) => {
        let canvas = event.target as HTMLCanvasElement;
        const rect = canvas.getBoundingClientRect();
        const deviceContext = (<DeviceContext>RcContext.instance.deviceContext);
        const left = deviceContext.left || 0;
        return (deviceContext.canvas.element?.cx || 0) + (event.clientX - rect.left - left) / scale;
    }

    static getY = (event: MouseEvent, scale: number) => {
        let canvas = event.target as HTMLCanvasElement;
        const rect = canvas.getBoundingClientRect();
        const deviceContext = (<DeviceContext>RcContext.instance.deviceContext);
        const top = deviceContext.top || 0;
        return (deviceContext.canvas.element?.cy || 0) + (event.clientY - rect.top - top) / scale;
    }

    static getTransformedMousePositionX = (event: MouseEvent, rotation: number, scale: number): number => {
        return Math.cos(rotation) * this.getX(event, scale) - Math.sin(rotation) * this.getY(event, scale);
    }

    static getTransformedMousePositionY = (event: MouseEvent, rotation: number, scale: number): number => {
        return Math.sin(rotation) * this.getX(event, scale) + Math.cos(rotation) * this.getY(event, scale);
    }

    static getUntransformedMousePositionX = (event: MouseEvent, rotation: number, scale: number): number => {
        const canvas: any = event.target;
        return (Math.cos(-rotation) * this.getUntransformedX(event, scale) - Math.sin(-rotation) * this.getUntransformedY(event, scale) - canvas.size.cx * scale);
    }

    static getUntransformedMousePositionY = (event: MouseEvent, rotation: number, scale: number): number => {
        const canvas: any = event.target;
        return (Math.sin(-rotation) * this.getUntransformedX(event, scale) + Math.cos(-rotation) * this.getUntransformedY(event, scale) - canvas.size.cy * scale);
    }

    static cloneMouseEventAsType = (e: MouseEvent): MouseEvent => {
        // We do not instantiate actual DOM event because some properties like currentTarget
        // cannot be set unless event is dispatched. However, we do not want to dispatch it
        // just store a copy to pass on to our logic
        return {
            type: e.type,
            // canBubble: e.canBubble,
            cancelable: e.cancelable,
            view: e.view,
            detail: e.detail,
            screenX: e.screenX,
            screenY: e.screenY,
            clientX: e.clientX,
            clientY: e.clientY,
            ctrlKey: e.ctrlKey,
            altKey: e.altKey,
            shiftKey: e.shiftKey,
            metaKey: e.metaKey,
            button: e.button,
            buttons: e.buttons,
            which: e.which,
            relatedTarget: e.relatedTarget,
            currentTarget: e.currentTarget,
            target: e.target,
            offsetX: e.offsetX,
            offsetY: e.offsetY
        } as MouseEvent;
    }

    static fixEvent = (event: any): MouseEvent => {
        if (event.offsetX == undefined) // this works for Firefox
        {
            let obj = event.target;
            event.offsetX = event.pageX;
            event.offsetY = event.pageY;
            do {
                event.offsetX -= obj.offsetLeft;
                event.offsetY -= obj.offsetTop;
            } while (obj = obj.offsetParent);
        }
        return event;
    }
};

/**
 * Implements mouse button tracking in a cross-browser way
 *
 * event.which is non-standard property (see https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/which)
 * and it has issue in Edge where on hover mouse button remains as 1 (left mouse button down): https://stackoverflow.com/q/30940290
 * On the other hand mouse.button and mouse.buttons are compatible by W3c standards but is not present on older and mobile browsers.
 * This class abstracts these incompatibilities and uses all of the above properties depending on what's available.
 *
 * Additionally, event.button and event.buttons are set as 0 on mouse-up which does not indicate
 * which mouse button went-up, without previously remembering which mouse button was down.
 * For this reason we track the state of pressed buttons across the events here.
 */
export class ButtonTracker {

    /**
     * Last event flags
     */
    private mouseFlags = 0;

    /**
     * Second last event flags
     */
    private pastMouseFlags = 0;

    trackMouseDown = (event: MouseEvent) => {
        this.pastMouseFlags = this.mouseFlags;

        if (typeof event.buttons !== 'undefined') {
            this.mouseFlags = event.buttons;
            return;
        }

        if (typeof event.button !== 'undefined') {
            switch (event.button) {
                case 0: this.mouseFlags |= 0x1; break;  // left button
                case 2: this.mouseFlags |= 0x2; break;  // right button
                case 1: this.mouseFlags |= 0x4; break;  // middle button
            }
            return;
        }

        if (typeof event.which !== 'undefined') {
            switch (event.which) {
                case 0: this.mouseFlags = 0; break;
                case 1: this.mouseFlags |= 0x1; break;
                case 2: this.mouseFlags |= 0x4; break;
                case 3: this.mouseFlags |= 0x2; break;
            }
        }
    }

    trackMouseUp = (event: any) => {
        this.pastMouseFlags = this.mouseFlags;

        if (typeof event.buttons !== 'undefined') {
            this.mouseFlags = event.buttons;
            return;
        }

        if (typeof event.button !== 'undefined') {
            switch (event.button) {
                case 0: this.mouseFlags &= 0xE; break;  // left button
                case 2: this.mouseFlags &= 0xD; break;  // right button
                case 1: this.mouseFlags &= 0xB; break;  // middle button
            }
            return;
        }

        if (typeof event.which !== 'undefined') {
            switch (event.which) {
                case 0: this.mouseFlags = 0; break;
                case 1: this.mouseFlags &= 0xE; break;
                case 2: this.mouseFlags &= 0xB; break;
                case 3: this.mouseFlags &= 0xD; break;
            }
        }
    }

    trackMouseMove = (event: any) => {
        this.pastMouseFlags = this.mouseFlags;

        if (typeof event.buttons !== 'undefined') {
            this.mouseFlags = event.buttons;
            return;
        }

        // event.button case is not reliable in case of mouse move, see:
        // "This property only guarantees to indicate which buttons are pressed
        // during events caused by pressing or releasing one or multiple buttons.
        // As such, it is not reliable for events such as mouseenter, mouseleave,
        // mouseover, mouseout or mousemove.
        // For more see: https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/button

        if (typeof event.which !== 'undefined') {
            switch (event.which) {
                case 0: this.mouseFlags = 0; break;
                case 1: this.mouseFlags |= 0x1; break;
                case 2: this.mouseFlags |= 0x4; break;
                case 3: this.mouseFlags |= 0x2; break;
            }
            return;
        }
    }

    /**
     * Indicates left mouse button state changed
     * from between last and second last tracked events
     */
    hasLeftChanged = () => (this.mouseFlags & 1) !== (this.pastMouseFlags & 1);

    hasRightChanged = () => ((this.mouseFlags >> 1) & 1) !== ((this.pastMouseFlags >> 1) & 1);

    hasMiddleChanged = () => ((this.mouseFlags >> 2) & 1) !== ((this.pastMouseFlags >> 2) & 1);

    /**
     * Indicates whether left mouse button is currently pressed
     */
    isLeftDown = () => (this.mouseFlags & 1) > 0;

    /**
     * Indicates whether left mouse button is currently pressed
     */
    isMiddleDown = () => ((this.mouseFlags >> 2) & 1) > 0;

    /**
     * Indicates whether left mouse button is currently pressed
     */
    isRightDown = () => ((this.mouseFlags >> 1) & 1) > 0;
}
